'use client'

import clsx from 'clsx'
import Link from 'next/link'

import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import { handleProgramaticScroll } from '@/_lib/utils'

import { matchesHeaderNavSubtreeActive } from '../../_utils/matchesHeaderNavSubtreeActive'

import type { ILink } from '../../_hooks'

export interface AHeaderHamburgerNavBranchProps {
	link: ILink
	branchKey: string
	onLinkClick: () => void
	pathWithoutLocale: string
	openBranches: Set<string>
	toggleBranch: (key: string) => void
	depth?: number
	/** Extra class on the expand/collapse label button (e.g. attention animation). */
	labelButtonClassName?: string
	/** When set, passed as React `key` on the label button to remount and retrigger animations. */
	labelButtonKey?: number
}

export const AHeaderHamburgerNavBranch = ({
	link,
	branchKey,
	onLinkClick,
	pathWithoutLocale,
	openBranches,
	toggleBranch,
	depth = 0,
	labelButtonClassName,
	labelButtonKey
}: AHeaderHamburgerNavBranchProps) => {
	const hasChildren = Boolean(link.children?.length)
	const isOpen = openBranches.has(branchKey)
	const isActive = matchesHeaderNavSubtreeActive(pathWithoutLocale, link)

	if (!hasChildren) {
		return (
			<li
				className={clsx(
					'header-hamburger-services__nav-leaf',
					depth > 0 && 'header-hamburger-services__nav-leaf--nested',
					isActive && 'active'
				)}
			>
				<Link
					href={nextLinkHrefFromPathWithOptionalHash(link.to)}
					onClick={e => {
						handleProgramaticScroll(e)
						onLinkClick()
					}}
					prefetch={link.prefetch ?? false}
				>
					{link.label}
				</Link>
			</li>
		)
	}

	return (
		<li
			className={clsx(
				'header-hamburger-services__nav-branch',
				isActive && 'active',
				'has-accordion'
			)}
		>
			<div className='header-hamburger-services__accordion-row'>
				<button
					key={labelButtonKey}
					type='button'
					className={clsx(
						'header-hamburger-services__accordion-row-label',
						labelButtonClassName
					)}
					aria-expanded={isOpen}
					onClick={() => toggleBranch(branchKey)}
				>
					{link.label}
				</button>
				<button
					type='button'
					className={clsx(
						'header-hamburger-services__accordion-toggle',
						isOpen && 'is-open'
					)}
					tabIndex={-1}
					onClick={() => toggleBranch(branchKey)}
				>
					<svg className='sico' aria-hidden>
						<use href='/assets/layout/icons.svg#s_ar1_d' />
					</svg>
				</button>
			</div>

			<div
				className={clsx(
					'header-hamburger-services__accordion',
					'header-hamburger-services__accordion--nav-tree',
					isOpen && 'is-open',
					depth > 0 && 'header-hamburger-services__accordion--nested'
				)}
			>
				<ul className='header-hamburger-services__accordion-list header-hamburger-services__accordion-list--always-visible'>
					{link.children!.map((child, index) => {
						const childKey = `${branchKey}/${index}`
						return (
							<AHeaderHamburgerNavBranch
								key={childKey}
								link={child}
								branchKey={childKey}
								onLinkClick={onLinkClick}
								pathWithoutLocale={pathWithoutLocale}
								openBranches={openBranches}
								toggleBranch={toggleBranch}
								depth={depth + 1}
							/>
						)
					})}
				</ul>
			</div>
		</li>
	)
}
